import React, { FC, Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { ClickAwayListener, Divider, Grid, List, ListItem, ListSubheader, Theme, Typography } from '@material-ui/core';
import Skeleton from 'react-loading-skeleton';
import axios, { CancelTokenSource } from 'axios';
import { differenceInCalendarDays } from 'date-fns';
import useOpenRecord from 'hooks/useOpenRecord';
import { TabRecordType } from 'constants/tabs';
import { StandardConfirmationDialog } from 'components/AppDialog';
import { GET_EDIT_ALL_NOTIFICATION_BASE_URL, GET_EDIT_INDIVIDUAL_NOTIFICATION_BASE_URL } from 'constants/url';
import { CurrentUserContext } from 'contexts/CurrentUserContext';

import { NOTIFICATION_FILTER_LABELS, NotificationTypeFilter, emptyFilterMessage, matchesTypeFilter } from '../constants';
import NotificationHeader from './NotificationHeader';
import NotificationItem from './NotificationItem';

interface Props {
  setOpenPopper?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingData?: boolean;
  notifications: NotificationModel[];
  setNotifications: React.Dispatch<React.SetStateAction<NotificationModel[]>>;
  setTotalNotification: React.Dispatch<React.SetStateAction<number>>;
  handleSnackbar?: (variant: 'success' | 'error', message: string) => void; // ✅ new
}

// How many rows the panel renders before asking. The feed itself is small enough to fetch in one
// go - this is about not laying out a hundred rows an admin will never scroll to.
const PAGE_SIZE = 20;

interface NotificationSection {
  key: string;
  label: string;
  items: NotificationModel[];
}

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    // The header and filters live inside this box, so a fixed 400px left room for barely two rows.
    maxHeight: '60vh',
    borderRadius: '5px'
  },
  groupHeader: {
    background: theme.palette.background.default,
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    lineHeight: '28px',
    paddingLeft: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  notFound: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4)
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'space-between',
    background: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  loadMoreAction: { fontWeight: theme.typography.fontWeightMedium }
}));

/**
 * Split the feed into the sections it is read in: outstanding work by the day it arrived, then
 * everything already resolved.
 *
 * Resolved items stay in one trailing group rather than being filed under their own day, so the
 * rule that unresolved always sits above resolved survives the grouping.
 */
const buildSections = (notifications: NotificationModel[], now: Date): NotificationSection[] => {
  const today: NotificationModel[] = [];
  const yesterday: NotificationModel[] = [];
  const earlier: NotificationModel[] = [];
  const resolved: NotificationModel[] = [];

  notifications.forEach(notification => {
    if (notification.status === 'resolved') {
      resolved.push(notification);
      return;
    }

    const days = differenceInCalendarDays(now, new Date(notification.createdAt));

    if (days < 1) today.push(notification);
    else if (days < 2) yesterday.push(notification);
    else earlier.push(notification);
  });

  return [
    { key: 'today', label: 'Today', items: today },
    { key: 'yesterday', label: 'Yesterday', items: yesterday },
    { key: 'earlier', label: 'Earlier', items: earlier },
    { key: 'resolved', label: 'Resolved', items: resolved }
  ].filter(section => section.items.length > 0);
};

const NotificationList: FC<Props> = props => {
  const openRecord = useOpenRecord();
  const classes = useStyles(props);

  const { isLoadingData, notifications = [], setNotifications, setTotalNotification, setOpenPopper, handleSnackbar } = props;

  const [selectedFilter, setSelectedFilter] = useState<NotificationTypeFilter>(NotificationTypeFilter.ALL);
  const [showResolved, setShowResolved] = useState<boolean>(true);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);

  // Mark-all dialog (optional)
  const [openMarkAllConfirmation, setOpenMarkAllConfirmation] = useState<boolean>(false);

  // Resolve confirmation
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [pendingResolveId, setPendingResolveId] = useState<number | null>(null);

  const { currentUser } = useContext(CurrentUserContext);

  // Drives the relative timestamps, the "clears in Xh" labels and the day grouping. The panel is
  // only mounted while it is open, and an empty feed has nothing to age, so an idle popper does no
  // work.
  const [now, setNow] = useState<Date>(() => new Date());
  const hasNotifications = notifications.length > 0;

  useEffect(() => {
    if (!hasNotifications) {
      return undefined;
    }

    const timer = setInterval(() => setNow(new Date()), 60000);

    return () => clearInterval(timer);
  }, [hasNotifications]);

  const handleOkMarkAll = async () => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    try {
      await axios.put(GET_EDIT_ALL_NOTIFICATION_BASE_URL, { q: 'unread', status: 'read' }, { cancelToken: cancelTokenSource.token });

      setNotifications(prev => prev.map(n => (n.status === 'unread' ? { ...n, status: 'read' } : n)));
      setTotalNotification(0);
      handleSnackbar && handleSnackbar('success', 'All marked as read');
    } catch (e) {
      handleSnackbar && handleSnackbar('error', 'Failed to mark all as read');
    } finally {
      setOpenMarkAllConfirmation(false); // only close dialog; keep popper open
    }
  };

  const markAsRead = async (notifId: number) => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
    await axios.put(GET_EDIT_INDIVIDUAL_NOTIFICATION_BASE_URL(notifId), { status: 'read' }, { cancelToken: cancelTokenSource.token });

    setNotifications(prev => {
      const next = prev.map(n => (n.id === notifId ? { ...n, status: 'read' } : n));
      setTotalNotification(next.filter(n => n.status === 'unread').length);
      return next;
    });
  };

  // Clearing noise should not cost a trip into the job and back out again.
  const handleMarkRead = async (notifId: number) => {
    try {
      await markAsRead(notifId);
    } catch (err) {
      handleSnackbar && handleSnackbar('error', 'Failed to mark as read');
      console.log(err);
    }
  };

  const markAsReadAndOpen = async (notification: NotificationModel) => {
    try {
      // Opening a resolved notification must leave its status alone: marking it read would clear
      // its resolution details on the server and quietly reopen it.
      if (notification.status !== 'resolved') {
        await markAsRead(notification.id);
      }

      // Opening job detail will close the popper (kept behavior)
      openRecord({ recordType: TabRecordType.JOB, recordId: notification.jobId });
      setOpenPopper && setOpenPopper(false);
    } catch (err) {
      console.log(err);
    }
  };

  const confirmResolve = (notifId: number) => {
    setPendingResolveId(notifId);
    setResolveDialogOpen(true);
  };

  const doResolve = async () => {
    if (pendingResolveId == null) return;
    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      await axios.put(GET_EDIT_INDIVIDUAL_NOTIFICATION_BASE_URL(pendingResolveId), { status: 'resolved' }, { cancelToken: cancelTokenSource.token });

      setNotifications(prev => {
        const next = [...prev];
        const idx = next.findIndex(n => n.id === pendingResolveId);
        if (idx !== -1) {
          const wasUnread = next[idx].status === 'unread';
          next[idx] = {
            ...next[idx],
            status: 'resolved',
            resolvedBy: currentUser?.displayName || 'Unknown',
            resolvedAt: new Date().toISOString()
          };
          if (wasUnread) {
            const count = next.filter(n => n.status === 'unread').length;
            setTotalNotification(count);
          }
        }
        return next;
      });

      handleSnackbar && handleSnackbar('success', 'Notification resolved');
    } catch (err) {
      handleSnackbar && handleSnackbar('error', 'Failed to resolve notification');
      console.log(err);
    } finally {
      setResolveDialogOpen(false); // only close dialog
      setPendingResolveId(null);
    }
  };

  // Undo is a single tap: it is immediately reversible with Resolve, so it gets no confirmation
  // dialog of its own. The server clears "resolvedBy"/"resolvedAt", which takes the notification
  // back out of the queue waiting to be cleared from the feed.
  const doUndo = async (notifId: number) => {
    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      await axios.put(GET_EDIT_INDIVIDUAL_NOTIFICATION_BASE_URL(notifId), { status: 'read' }, { cancelToken: cancelTokenSource.token });

      setNotifications(prev => prev.map(n => (n.id === notifId ? { ...n, status: 'read', resolvedBy: undefined, resolvedAt: undefined } : n)));

      handleSnackbar && handleSnackbar('success', 'Resolution undone');
    } catch (err) {
      handleSnackbar && handleSnackbar('error', 'Failed to undo resolution');
      console.log(err);
    }
  };

  // Counts answer "where is the work", so they only ever count what is still outstanding.
  const counts = useMemo(() => {
    const outstanding = notifications.filter(notif => notif.status !== 'resolved');

    return NOTIFICATION_FILTER_LABELS.reduce((totals, { filter }) => {
      totals[filter] = outstanding.filter(notif => matchesTypeFilter(notif, filter)).length;
      return totals;
    }, {} as Record<NotificationTypeFilter, number>);
  }, [notifications]);

  const unreadCount = useMemo(() => notifications.filter(notif => notif.status === 'unread').length, [notifications]);

  const filteredNotifications = useMemo(() => {
    const byType = notifications.filter(notif => matchesTypeFilter(notif, selectedFilter));

    return showResolved ? byType : byType.filter(notif => notif.status !== 'resolved');
  }, [notifications, selectedFilter, showResolved]);

  // Changing what is on screen starts the count again, so a new filter never opens half-expanded.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedFilter, showResolved]);

  const visibleNotifications = useMemo(() => filteredNotifications.slice(0, visibleCount), [filteredNotifications, visibleCount]);
  const hasMore = filteredNotifications.length > visibleNotifications.length;

  const sections = useMemo(() => buildSections(visibleNotifications, now), [visibleNotifications, now]);

  const renderSkeleton = () =>
    [1, 2, 3, 4, 5].map(index => (
      <Fragment key={index}>
        <ListItem alignItems='flex-start' button>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Skeleton width={150} />
              <Skeleton width={150} />
            </Grid>
          </Grid>
        </ListItem>
        {index !== 4 && <Divider />}
      </Fragment>
    ));

  const renderNoResult = (message: string) => (
    <Typography variant='body2' align='center' color='textSecondary' className={classes.notFound}>
      {message}
    </Typography>
  );

  const renderList = () =>
    sections.map((section, sectionIndex) => (
      <Fragment key={section.key}>
        <ListSubheader disableSticky disableGutters className={classes.groupHeader}>
          {section.label}
        </ListSubheader>

        {section.items.map((notif, itemIndex) => (
          <NotificationItem
            key={notif.id}
            notification={notif}
            showDivider={!(sectionIndex === sections.length - 1 && itemIndex === section.items.length - 1)}
            now={now}
            onOpen={markAsReadAndOpen}
            onResolve={confirmResolve}
            onUndo={doUndo}
            onMarkRead={handleMarkRead}
          />
        ))}
      </Fragment>
    ));

  const content = isLoadingData
    ? renderSkeleton()
    : filteredNotifications.length === 0
    ? renderNoResult(hasNotifications ? emptyFilterMessage(selectedFilter) : "You're all caught up")
    : renderList();

  const renderLoadMore = () => (
    <ListItem button className={classes.loadMore} onClick={() => setVisibleCount(count => count + PAGE_SIZE)}>
      <Typography variant='caption' color='textSecondary'>
        Showing {visibleNotifications.length} of {filteredNotifications.length}
      </Typography>
      <Typography variant='body2' color='primary' className={classes.loadMoreAction}>
        Load more
      </Typography>
    </ListItem>
  );

  // ✅ Prevent click-away close while dialogs are open (they render in a portal)
  const clickAwayEnabled = !resolveDialogOpen && !openMarkAllConfirmation;

  const notificationList = (
    <List
      subheader={
        <NotificationHeader
          selectedFilter={selectedFilter}
          counts={counts}
          unreadCount={unreadCount}
          showResolved={showResolved}
          onFilterChange={setSelectedFilter}
          onShowResolvedChange={setShowResolved}
          onMarkAllClick={() => setOpenMarkAllConfirmation(true)}
        />
      }
      className={classes.list}
    >
      {content}
      {!isLoadingData && hasMore && renderLoadMore()}
    </List>
  );

  return (
    <Fragment>
      {clickAwayEnabled ? (
        <ClickAwayListener onClickAway={() => setOpenPopper && setOpenPopper(false)}>{notificationList}</ClickAwayListener>
      ) : (
        // When a dialog is open, render without ClickAwayListener so the popper stays open
        notificationList
      )}

      {/* Mark all as read dialog */}
      <StandardConfirmationDialog
        variant={'warning'}
        message='Are you sure want to make all as read?'
        okLabel='OK'
        cancelLabel='cancel'
        open={openMarkAllConfirmation}
        handleClose={() => setOpenMarkAllConfirmation(false)} // only close dialog
        onConfirm={handleOkMarkAll}
      />

      {/* Resolve confirmation */}
      <StandardConfirmationDialog
        variant={'warning'}
        message='are you sure want to resolve this job?'
        secondMessage='It will be moved to the Resolved section.'
        okLabel='Resolve'
        cancelLabel='Cancel'
        open={resolveDialogOpen}
        handleClose={() => {
          setResolveDialogOpen(false);
          setPendingResolveId(null);
        }}
        onConfirm={doResolve}
      />
    </Fragment>
  );
};

export default NotificationList;
